/* GoBuildIt — gbi-search.js (owner C2). <gbi-search>: open search field with a rolling placeholder,
   popular chips, predictive results through Section Rendering (sections/gbi-predictive-search.liquid).
   Spec: docs/specs/gbi-header-search-cart-spec.md §4. Vanilla ES2019, no globals besides the element.
   Contract with C1 (§2.3): public open({source}) / close({reason}); emits gbi:search-open /
   gbi:search-close (bubbling); reads data-state="condensed" from the closest gbi-header (CSS only). */
(function () {
  'use strict';
  if (window.customElements.get('gbi-search')) return;

  var mqDesk = window.matchMedia('(min-width: 990px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ROLL_AFTER_LOAD = 1800;   // ms after window load before the typewriter may start (speed audit #9)
  var ROLL_IDLE_MAX = 2000;     // then wait for an idle moment, at most this long

  function track(action, params) {
    try { if (window.GBI && typeof window.GBI.track === 'function') window.GBI.track('header_search', Object.assign({ action: action }, params || {})); } catch (e) { /* ignore */ }
  }
  function emit(el, name, detail) {
    try { el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} })); } catch (e) { /* ignore */ }
  }
  function fill(str, vars) {
    return String(str || '').replace(/\[([a-z_]+)\]/g, function (m, k) { return vars && vars[k] != null ? vars[k] : m; });
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  class GbiSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;            // safe to move in the DOM: init once, never torn down
      this._ready = true;
      var cfgEl = this.querySelector('script[data-gbi-search-config]');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}') || {}; } catch (e) { this.cfg = {}; }
      this.cfg.min = this.cfg.min || 2;
      this.input = this.querySelector('.gbi-search__input');
      this.form = this.querySelector('.gbi-search__form');
      this.panel = this.querySelector('.gbi-search__panel');
      this.viewEmpty = this.querySelector('[data-view="empty"]');
      this.viewResults = this.querySelector('[data-view="results"]');
      this.status = this.querySelector('.gbi-search__status');
      this.clearBtn = this.querySelector('.gbi-search__clear');
      this.cancelBtn = this.querySelector('.gbi-search__cancel');
      this.term = this.querySelector('.gbi-search__ph-term');
      this.roll = this.querySelector('.gbi-search__ph-roll');
      if (!this.input || !this.panel) return;
      this.cache = new Map();
      this.ctrl = null;
      this.timer = null;
      this.skelTimer = null;
      this.retryTimer = null;
      this._inerted = [];
      this.isOpen = false;
      this.q = '';
      this.bind();
      this.startRoll();
    }

    /* ------------------------------------------------------------ events */

    bind() {
      var self = this;
      // Open on focus, never on pointerdown: the phone sheet moves the field, and a pointerdown
      // open would let the same tap land on whatever slides under the finger.
      this.input.addEventListener('focus', function () {
        if (self._noReopen) { self._noReopen = false; return; }   // focus handed back by close(): stay closed
        if (!self.isOpen) self.open({ source: self.sourceHint(), focus: false });
      });
      this.input.addEventListener('input', function () { self.onInput(); });
      this.input.addEventListener('keydown', function (e) { self.onKey(e); });
      this.form.addEventListener('submit', function (e) {
        var q = self.input.value.trim();
        if (!q) { e.preventDefault(); return; }
        track('submit', { q_len: q.length });
      });
      if (this.clearBtn) this.clearBtn.addEventListener('click', function () {
        self.input.value = '';
        self.onInput();
        self.input.focus();
      });
      // detail 0 = activated from the keyboard (Enter/Space), not a tap.
      if (this.cancelBtn) this.cancelBtn.addEventListener('click', function (e) { self.close({ reason: 'cancel', kbd: e.detail === 0 }); });
      // The phone sheet is modal: Esc anywhere inside it closes it (focus may sit on a result, not the field).
      this.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && self.isOpen && e.target !== self.input) { e.preventDefault(); self.close({ reason: 'esc' }); }
      });
      this.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[data-gbi-s]');
        if (!a || !self.contains(a)) return;
        var kind = a.getAttribute('data-gbi-s');
        track(kind === 'chip' ? 'chip' : (kind === 'all' ? 'see_all' : 'suggestion'), {
          type: kind, position: Number(a.getAttribute('data-pos')) || 0, q_len: self.q.length,
          label: kind === 'chip' ? (a.getAttribute('data-label') || '') : undefined
        });
      });
      this._off = [];
      var listen = function (t, type, fn, o) { t.addEventListener(type, fn, o); self._off.push(function () { t.removeEventListener(type, fn, o); }); };
      listen(document, 'pointerdown', function (e) {
        if (!self.isOpen || !mqDesk.matches) return;
        if (self.contains(e.target)) return;
        if (e.target.closest && e.target.closest('[data-gbi-search-keep]')) return;
        self.close({ reason: 'outside' });
      });
      listen(document, 'keydown', function (e) {
        if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || !mqDesk.matches) return;
        var t = e.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        if (!self.offsetParent && !self.getClientRects().length) return;
        e.preventDefault();
        self.open({ source: 'kbd' });
      });
      var onMq = function () { if (self.isOpen) self.close({ reason: 'resize' }); };
      if (mqDesk.addEventListener) listen(mqDesk, 'change', onMq);          // removed on disconnect (editor re-renders)
      else { mqDesk.addListener(onMq); this._off.push(function () { mqDesk.removeListener(onMq); }); }
      // A pre-filled field (search page: value = search.terms) shows its clear button from the start.
      if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
    }

    disconnectedCallback() {
      (this._off || []).forEach(function (off) { off(); });
      this._off = [];
      if (this.rollTimer) { clearInterval(this.rollTimer); this.rollTimer = null; }
      if (this._armT) { clearTimeout(this._armT); this._armT = null; }
      if (this.rollIO) this.rollIO.disconnect();
      if (this.ctrl) this.ctrl.abort();
      clearTimeout(this.retryTimer);
      this.modal(false);
      document.documentElement.classList.remove('gbi-search-lock');
    }

    sourceHint() {
      var hdr = this.closest('gbi-header');
      return hdr && hdr.getAttribute('data-state') === 'condensed' ? 'bar' : 'row';
    }

    /* ---------------------------------------------------------- open/close */

    open(opts) {
      opts = opts || {};
      if (this.isOpen) { if (opts.focus !== false) this.input.focus(); return; }
      this.isOpen = true;
      var active = document.activeElement;
      // Opened by focusing the open field itself (phone row): the field is the opener (QA M8, spec §4.1).
      this._opener = active === this.input ? this.input : (active && active !== document.body && !this.contains(active) ? active : null);
      this.classList.add('is-open');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (!mqDesk.matches) document.documentElement.classList.add('gbi-search-lock');
      // Pre-filled field (search page): open on its results, not on the popular panel under "gt3".
      var pre = this.input.value.trim();
      // Always re-request (the cache answers at once): a fetch aborted by an early close must not leave a stuck skeleton.
      var fetchPre = !opts.typing && pre.length >= this.cfg.min;   // typing fetches on its own
      if (fetchPre) this.q = pre;
      this.render();
      if (fetchPre) { this.skeleton(); this.fetchResults(pre); }
      emit(this, 'gbi:search-open', { source: opts.source || 'api' });   // sync: C1 shows the slot before the focus below
      if (!mqDesk.matches) this.modal(true);
      track('open', { source: opts.source || 'api' });
      if (opts.focus !== false) {
        var input = this.input;
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      }
      this.pauseRoll();
    }

    close(opts) {
      if (!this.isOpen) return;
      opts = opts || {};
      this.isOpen = false;
      this.classList.remove('is-open');
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.setActive(null);
      document.documentElement.classList.remove('gbi-search-lock');
      if (this.ctrl) { this.ctrl.abort(); this.ctrl = null; }
      clearTimeout(this.retryTimer);
      this.modal(false);
      if (opts.reason === 'cancel' || opts.reason === 'outside' || opts.reason === 'resize' || !mqDesk.matches) {
        if (document.activeElement === this.input || this.contains(document.activeElement)) this.input.blur();
      }
      emit(this, 'gbi:search-close', { reason: opts.reason || 'api' });
      // Cancel / Esc on the phone sheet: focus goes back to what opened it (the bar's search icon), not to <body>.
      var back = this._opener;
      this._opener = null;
      // Back to the field only for keyboard closes: a programmatic focus after a tap would raise the phone keyboard again.
      if (back === this.input && !(opts.reason === 'esc' || opts.kbd)) back = null;
      if ((opts.reason === 'cancel' || opts.reason === 'esc') && !mqDesk.matches && back && back.isConnected) {
        if (back === this.input) this._noReopen = true;            // one-shot: the focus below must not reopen the sheet
        try { back.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        if (back === this.input && document.activeElement !== this.input) this._noReopen = false;
      }
      this.resumeRoll();
    }

    // Phone sheet = modal dialog: role + aria-modal, and everything outside it inert (Tab and swipe stay inside).
    // Siblings of every ancestor up to <body> are made inert; only the nodes made inert here are restored.
    modal(on) {
      if (on) {
        if (this._inerted.length) return;
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-label', (this.cfg.strings && this.cfg.strings.dialog) || 'Search');
        var self = this;
        var node = this;
        while (node && node !== document.body && node.parentElement) {
          var parent = node.parentElement;
          Array.prototype.forEach.call(parent.children, function (sib) {
            if (sib === node || sib.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sib.tagName)) return;
            sib.inert = true;
            self._inerted.push(sib);
          });
          node = parent;
        }
      } else {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        this.removeAttribute('aria-label');
        this._inerted.forEach(function (n) { n.inert = false; });
        this._inerted = [];
      }
    }

    /* ------------------------------------------------------------- typing */

    onInput() {
      var q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
      if (!this.isOpen) this.open({ source: this.sourceHint(), focus: false, typing: true });
      clearTimeout(this.timer);
      clearTimeout(this.retryTimer);
      this.q = q;
      if (q.length < this.cfg.min) {
        // Below the minimum: drop the pending skeleton and the request, or they would replace the empty panel.
        clearTimeout(this.skelTimer);
        if (this.ctrl) { this.ctrl.abort(); this.ctrl = null; }
        this.render();
        return;
      }
      var self = this;
      this.timer = setTimeout(function () { self.fetchResults(q); }, 180);
    }

    url(q) {
      var p = this.cfg.params || 'resources[type]=product,collection,query&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last';
      return (this.cfg.url || '/search/suggest') + '?q=' + encodeURIComponent(q) + '&' + p + '&section_id=' + encodeURIComponent(this.cfg.section || 'gbi-predictive-search');
    }

    fetchResults(q, attempt) {
      attempt = attempt || 0;
      var key = q.toLowerCase();
      if (this.cache.has(key)) {
        clearTimeout(this.skelTimer);
        if (this.ctrl) { this.ctrl.abort(); this.ctrl = null; }
        this.show(q, this.cache.get(key));
        return;
      }
      if (this.ctrl) this.ctrl.abort();
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this.ctrl = ctrl;
      var self = this;
      var current = function () { return self.input.value.trim().toLowerCase() === key; };
      if (!attempt) {
        clearTimeout(this.skelTimer);
        this.skelTimer = setTimeout(function () { if (current()) self.skeleton(); }, 150);
      }
      var req = window.GBI_SEARCH_MOCK ? window.GBI_SEARCH_MOCK(q) : fetch(this.url(q), { signal: ctrl ? ctrl.signal : undefined, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) { var err = new Error(String(r.status)); err.status = r.status; throw err; } return r.text(); });
      req.then(function (html) {
        if (ctrl && ctrl.signal.aborted) return;
        clearTimeout(self.skelTimer);
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var root = doc.getElementById('gbi-ps');
        var data = { queries: [], collections: [], products: [] };
        if (root) {
          ['queries', 'collections', 'products'].forEach(function (g) {
            var ul = root.querySelector('[data-group="' + g + '"]');
            if (ul) data[g] = Array.prototype.slice.call(ul.children);
          });
        }
        self.cache.set(key, data);
        if (self.cache.size > 24) self.cache.delete(self.cache.keys().next().value);
        if (current()) self.show(q, data);
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (!current() || !self.isOpen) return;                  // a newer query owns the view
        var status = (err && err.status) || 0;                   // 0 = network error / offline
        if (!attempt && (status === 429 || status >= 500 || status === 0)) {
          // One retry after 1.5 s (the store rate-limits bursts); the skeleton stays meanwhile. Errors are never cached.
          self.retryTimer = setTimeout(function () { if (current() && self.isOpen) self.fetchResults(q, 1); }, 1500);
          return;
        }
        clearTimeout(self.skelTimer);
        self.show(q, { queries: [], collections: [], products: [], error: true });
      });
    }

    /* ------------------------------------------------------------ render */

    render() {
      var showResults = this.q.length >= this.cfg.min;
      this.viewEmpty.hidden = showResults;
      this.viewResults.hidden = !showResults;
      this.input.setAttribute('aria-controls', (showResults ? this.viewResults : this.viewEmpty).id);
      this.setActive(null);
    }

    skeleton() {
      var box = this.viewResults.querySelector('[data-slot="products"]');
      if (!box) return;
      this.viewEmpty.hidden = true;
      this.viewResults.hidden = false;
      box.innerHTML = '<li class="gbi-search__skel" aria-hidden="true"><span></span><span></span><span></span></li>';
    }

    // Collection rows: curated label, thumbnail and count from the header menu (by handle), when known.
    curate(li) {
      var a = li.querySelector('a[data-handle]');
      var m = a && this.cfg.menu ? this.cfg.menu[a.getAttribute('data-handle')] : null;
      if (!m) return li;
      var label = a.querySelector('.gbi-hdr-row__label span');
      var img = a.querySelector('img');
      var count = a.querySelector('.gbi-hdr-row__count');
      if (label && m.l) label.textContent = m.l;
      if (img && m.i) { img.src = m.i; img.removeAttribute('srcset'); }
      if (count && m.c != null) count.textContent = m.c;
      return li;
    }

    // "gt3 german racer 430pcs" → "gt3 german racer": drop a trailing piece count, like gbi-clean-title (QA M11).
    cleanQuery(li) {
      var box = li.querySelector('.gbi-search-q__text');
      if (!box) return li;
      var re = /\s*\d+\s*pcs\s*$/i;
      var walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT, null);
      var last = null, n;
      while ((n = walker.nextNode())) { if (n.nodeValue.trim()) last = n; }
      if (last && re.test(last.nodeValue) && box.textContent.replace(re, '').trim()) last.nodeValue = last.nodeValue.replace(re, '');
      return li;
    }

    show(q, data) {
      var self = this;
      var seen = Object.create(null);
      var products = (data.products || []).filter(function (li) {         // dedupe duplicate listings by title
        var t = (li.getAttribute('data-title') || li.textContent || '').trim().toLowerCase();
        if (seen[t]) return false;
        seen[t] = true;
        return true;
      }).slice(0, 4);
      // Only collections the header menu knows (curated label, thumbnail, count). App leftovers never show.
      var menu = this.cfg.menu || {};
      var collections = (data.collections || []).filter(function (li) {
        var a = li.querySelector('a[data-handle]');
        return a && Object.prototype.hasOwnProperty.call(menu, a.getAttribute('data-handle'));
      }).slice(0, 3);
      // A popular term that matches the query brings its collection along ("tank" → Tanks & vehicles).
      var ql = q.toLowerCase();
      var have = {};
      collections.forEach(function (li) { var a = li.querySelector('a[data-handle]'); if (a) have[a.getAttribute('data-handle')] = 1; });
      (this.cfg.terms || []).forEach(function (t) {
        if (collections.length >= 3 || !t.h || have[t.h] || !self.cfg.menu || !self.cfg.menu[t.h]) return;
        var words = (t.k || t.l || '').toLowerCase().split(/\s*,\s*|\s+/);
        if (words.some(function (w) { return w && (w.indexOf(ql) === 0 || ql.indexOf(w) === 0); })) {
          var m = self.cfg.menu[t.h];
          var li = document.createElement('li');
          li.setAttribute('role', 'option');
          li.innerHTML = '<a class="gbi-hdr-row" data-handle="' + esc(t.h) + '" href="' + esc(m.u) + '" tabindex="-1"><span class="gbi-hdr-row__thumb"><img src="' + esc(m.i) + '" alt="" width="48" height="48" loading="lazy"></span><span class="gbi-hdr-row__label"><span>' + esc(m.l) + '</span></span><span class="gbi-hdr-row__count">' + esc(m.c) + '</span></a>';
          collections.push(li);
          have[t.h] = 1;
        }
      });
      var qSeen = Object.create(null);
      var queries = (data.queries || []).filter(function (li) {          // after the pcs cut two suggestions can match
        var t = self.cleanQuery(li.cloneNode(true)).textContent.trim().toLowerCase();
        if (qSeen[t]) return false;
        qSeen[t] = true;
        return true;
      }).slice(0, 4);
      var slots = {
        queries: this.viewResults.querySelector('[data-slot="queries"]'),
        collections: this.viewResults.querySelector('[data-slot="collections"]'),
        products: this.viewResults.querySelector('[data-slot="products"]')
      };
      var n = 0;
      function put(slot, list, kind) {
        if (!slot) return;
        slot.innerHTML = '';
        list.forEach(function (li, i) {
          var node = li.cloneNode(true);
          if (kind === 'collections') self.curate(node);
          if (kind === 'queries') self.cleanQuery(node);
          node.setAttribute('role', 'option');
          node.id = 'GbiSearchOpt-' + kind + '-' + (i + 1);
          var a = node.querySelector('a');
          if (a) { a.setAttribute('tabindex', '-1'); a.setAttribute('data-gbi-s', kind); a.setAttribute('data-pos', String(i + 1)); }
          slot.appendChild(node);
          n++;
        });
        var group = slot.closest('.gbi-search__group');
        if (group) group.hidden = list.length === 0;
      }
      put(slots.queries, queries, 'queries');
      put(slots.collections, collections, 'collections');
      put(slots.products, products, 'products');
      var all = this.viewResults.querySelector('[data-slot="all"]');
      if (all) {
        all.href = (this.cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
        var lab = all.querySelector('span');
        if (lab) lab.textContent = fill(this.cfg.strings && this.cfg.strings.all || 'See all results for “[terms]”', { terms: q });
      }
      var strings = this.cfg.strings || {};
      var err = !!data.error;
      var none = this.viewResults.querySelector('[data-slot="none"]');
      if (none) {
        none.hidden = n > 0;
        var nt = none.querySelector('[data-slot="none-text"]');
        // A failed request is not "no results": say search is busy and keep the way to the full results page.
        if (nt) nt.innerHTML = err ? esc(strings.error || 'Search is busy right now. Press Enter to see all results.')
          : fill(esc(strings.none || 'No results for “[terms]”.'), { terms: '<b>' + esc(q) + '</b>' });
        var chips = none.querySelector('[data-slot="none-chips"]');
        if (chips) chips.hidden = err;
        var errBox = none.querySelector('[data-slot="err"]');
        if (errBox) errBox.hidden = !err;
        var errAll = none.querySelector('[data-slot="err-all"]');
        if (errAll && all) {
          errAll.href = all.href;
          var el = errAll.querySelector('span');
          if (el) el.textContent = fill(strings.all || 'See all results for “[terms]”', { terms: q });
        }
      }
      var main = this.viewResults.querySelector('.gbi-search__grid');
      if (main) main.hidden = n === 0;
      if (this.status) this.status.textContent = n ? fill(strings.count || '[count] suggestions', { count: n }) : (nt ? nt.textContent.trim() : '');
      this.render();
    }

    /* ----------------------------------------------------------- keyboard */

    options() {
      var view = this.viewResults.hidden ? this.viewEmpty : this.viewResults;
      return Array.prototype.filter.call(view.querySelectorAll('[role="option"]'), function (o) { return o.offsetParent !== null; });
    }

    setActive(opt) {
      var prev = this.querySelector('[role="option"].is-active');
      if (prev) { prev.classList.remove('is-active'); prev.setAttribute('aria-selected', 'false'); }
      if (!opt) { this.input.removeAttribute('aria-activedescendant'); return; }
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', opt.id);
      try { opt.scrollIntoView({ block: 'nearest' }); } catch (e) { /* ignore */ }
    }

    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (this.isOpen) { e.preventDefault(); this.close({ reason: 'esc' }); }
        else if (this.input.value) {
          // Closed + filled: Esc only clears. It never reopens the panel (QA S-ESC-REOPEN, spec §4.1).
          e.preventDefault();
          this.input.value = '';
          this.q = '';
          if (this.clearBtn) this.clearBtn.hidden = true;
          clearTimeout(this.timer);
          clearTimeout(this.skelTimer);
          clearTimeout(this.retryTimer);
          if (this.ctrl) { this.ctrl.abort(); this.ctrl = null; }
          this.render();
        }
        return;
      }
      if (e.key === 'Tab' && this.isOpen && mqDesk.matches) { this.close({ reason: 'tab' }); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      if (!this.isOpen) { if (e.key !== 'Enter') { e.preventDefault(); this.open({ source: 'kbd' }); } return; }
      var opts = this.options();
      var cur = this.querySelector('[role="option"].is-active');
      var i = opts.indexOf(cur);
      if (e.key === 'Enter') {
        if (cur) { e.preventDefault(); var a = cur.querySelector('a'); if (a) a.click(); }
        return;                                                     // no option: the form submits to /search
      }
      if (!opts.length) return;
      e.preventDefault();
      if (e.key === 'ArrowDown') i = i < opts.length - 1 ? i + 1 : 0;
      else i = i > 0 ? i - 1 : opts.length - 1;
      this.setActive(opts[i]);
    }

    /* --------------------------------------------------- typewriter placeholder (v1.1)
       Types a real top search letter by letter, holds it, backspaces fast, types the next.
       ≈2.5 s per word. Loops while the field is idle and on screen; pauses while the field is
       focused or filled (pauseRoll/resumeRoll), when the tab is hidden or the bar is off screen.
       Reduced motion: one static term, no animation.
       Speed audit #9 (2026-10-13): typing starts only once the page has loaded and gone quiet
       (window load + ROLL_AFTER_LOAD + an idle moment, at most ROLL_IDLE_MAX later). Until then the
       server-rendered term ("Search clocks…") stays as a still placeholder, so the field is never
       empty and nothing repaints while the page loads. It also stays still while the field has text. */

    startRoll() {
      var terms = (this.cfg.roll || []).filter(Boolean).map(function (t) {
        return String(t).replace(/\s*(…|\.\.\.)\s*$/, '');
      }).filter(Boolean);
      this.rollTerms = terms;
      if (!this.term || !terms.length) return;
      this.term.classList.add('is-in');
      if (mqReduce.matches || terms.length < 2) {
        if (this.cfg.staticTerm && mqReduce.matches) this.term.textContent = this.cfg.staticTerm;
        return;
      }
      // Start with the first word fully shown, then delete it and move on.
      this.tw = { i: 0, pos: terms[0].length, phase: 'hold' };
      var self = this;
      this.rollVisible = true;
      if ('IntersectionObserver' in window) {
        this.rollIO = new IntersectionObserver(function (en) { self.rollVisible = en[0].isIntersecting; self.syncRoll(); });
        this.rollIO.observe(this.input);
      }
      var listen = function (t, type, fn, o) { t.addEventListener(type, fn, o); self._off.push(function () { t.removeEventListener(type, fn, o); }); };
      listen(document, 'visibilitychange', function () { self.syncRoll(); });
      this.armRoll();
    }
    // Wait for window load, then ROLL_AFTER_LOAD, then an idle moment (or ROLL_IDLE_MAX), then type.
    armRoll() {
      var self = this;
      var go = function () {
        self._armT = null;
        if (self.rollArmed || self.rollDone || !self.isConnected) return;
        self.rollArmed = true;
        // The typed look (caret, first word without "…") only appears now, together with the first move.
        self.term.textContent = self.rollTerms[0];
        self.term.classList.add('is-typing', 'is-holding');
        self.syncRoll();
      };
      var idle = function () {
        self._armT = null;
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(go, { timeout: ROLL_IDLE_MAX });
        else go();
      };
      var afterLoad = function () { self._armT = setTimeout(idle, ROLL_AFTER_LOAD); };
      if (document.readyState === 'complete') afterLoad();
      else {
        window.addEventListener('load', afterLoad, { once: true });
        this._off.push(function () { window.removeEventListener('load', afterLoad); });
      }
    }
    stopRoll() {
      if (this.rollDone) return;
      this.rollDone = true;
      this.syncRoll();
      if (this.rollIO) { this.rollIO.disconnect(); this.rollIO = null; }
    }
    canRoll() {
      return !this.rollDone && !this.rollPaused && this.rollVisible !== false && !document.hidden && !this.input.value;
    }
    syncRoll() {
      var run = !!this.tw && !!this.rollArmed && this.canRoll() && !mqReduce.matches;
      if (run && !this.rollTimer) {
        var self = this;
        this.rollTimer = setTimeout(function () { self.twTick(); }, this.tw.phase === 'hold' ? 1200 : 90);
      } else if (!run && this.rollTimer) {
        clearTimeout(this.rollTimer);
        this.rollTimer = null;
      }
    }
    pauseRoll() { this.rollPaused = true; this.syncRoll(); }
    resumeRoll() { this.rollPaused = false; this.syncRoll(); }
    twTick() {
      this.rollTimer = null;
      if (!this.term || !this.tw) return;
      var T = this.rollTerms;
      var st = this.tw;
      var delay;
      if (st.phase === 'type') {
        st.pos += 1;
        if (st.pos >= T[st.i].length) { st.pos = T[st.i].length; st.phase = 'hold'; delay = 1300; }
        else delay = 55 + Math.floor(Math.random() * 35);          // 55–90 ms per letter, feels typed
      } else if (st.phase === 'hold') {
        st.phase = 'delete'; delay = 30;
      } else if (st.phase === 'delete') {
        st.pos -= 1;
        if (st.pos <= 0) { st.pos = 0; st.phase = 'gap'; delay = 260; }
        else delay = 30;                                             // backspace is quicker than typing
      } else {
        st.i = (st.i + 1) % T.length; st.pos = 0; st.phase = 'type'; delay = 90;
      }
      this.term.textContent = T[st.i].slice(0, st.pos);
      this.term.classList.toggle('is-holding', st.phase === 'hold');
      if (!this.canRoll()) return;
      var self = this;
      this.rollTimer = setTimeout(function () { self.twTick(); }, delay);
    }
  }

  window.customElements.define('gbi-search', GbiSearch);
})();
